import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CarTypeRequest } from '../../../../interfaces/carTypeRequest';
import { CarTypeResponse } from '../../../../interfaces/carTypeResponse';
import { CarService } from '../../../../providers/car/car.service';
import { DestroyableComponent } from '../../../utils/destroyable.component';
import { ICarFileds } from '../create-lead/create-lead.component';

export const carFields: ICarFileds = {
  model: {
    name: 'model',
    validator: ['', [Validators.required]],
  },
  make: {
    name: 'make',
    validator: ['', [Validators.required]],
  },
  year: {
    name: 'year',
    validator: ['', [Validators.required]],
  },
  type: {
    name: 'type',
    validator: ['', [Validators.required]],
  },
};

@Component({
  selector: 'app-create-lead-car',
  templateUrl: './create-lead-car.component.html',
  styleUrls: ['./create-lead-car.component.scss'],
})
export class CreateLeadCarComponent extends DestroyableComponent implements OnInit {
  private static readonly FIRST_CAR_YEAR = 1957;

  @Output() OnRemove = new EventEmitter<number>();
  @Input() form: FormGroup;
  @Input() index: number;

  fields = carFields;
  carYearOptions: string[] = [];
  carMakeOptions$: Observable<string[]>;
  carModelOptions$: Observable<string[]>;
  carTypeOptions$: Observable<string[]>;

  private readonly selectedMake$ = new BehaviorSubject<string>('');
  private readonly selectedModel$ = new BehaviorSubject<string>('');

  constructor(private readonly carService: CarService) {
    super();
  }

  ngOnInit() {
    this.carYearOptions = this.getCarYears();
    this.selectedMake$.next(this.trimValue(this.form.get(this.fields.make.name).value));
    this.selectedModel$.next(this.trimValue(this.form.get(this.fields.model.name).value));

    this.carMakeOptions$ = this.getFieldOptions({ field: 'make' }, 'make').pipe(shareReplay(1));
    this.carModelOptions$ = this.selectedMake$.pipe(
      switchMap((make) => make
        ? this.getFieldOptions({ field: 'model', make }, 'model')
        : of([])),
      shareReplay(1),
    );
    this.carTypeOptions$ = combineLatest([this.selectedMake$, this.selectedModel$]).pipe(
      switchMap(([make, model]) => make && model
        ? this.getFieldOptions({ field: 'type', make, model }, 'type')
        : of([])),
      tap((types) => {
        const typeControl = this.form.get(this.fields.type.name);
        const currentType = typeControl.value;

        if (!types.length) {
          typeControl.setValue('', { emitEvent: false });
          return;
        }

        if (types.length === 1) {
          typeControl.setValue(types[0], { emitEvent: false });
          return;
        }

        if (currentType && !types.includes(currentType)) {
          typeControl.setValue('', { emitEvent: false });
        }
      }),
      takeUntil(this.onDestroy),
      shareReplay(1),
    );
  }

  onYearChanged(year: string) {
    this.form.patchValue({
      [this.fields.year.name]: year,
    }, { emitEvent: false });
  }

  onMakeChanged(make: string) {
    this.selectedMake$.next(this.trimValue(make));
    this.form.patchValue({
      [this.fields.make.name]: make,
      [this.fields.model.name]: '',
      [this.fields.type.name]: '',
    }, { emitEvent: false });
    this.selectedModel$.next('');
  }

  onModelChanged(model: string) {
    this.selectedModel$.next(this.trimValue(model));
    this.form.patchValue({
      [this.fields.model.name]: model,
      [this.fields.type.name]: '',
    }, { emitEvent: false });
  }

  onTypeChanged(type: string) {
    this.form.patchValue({
      [this.fields.type.name]: type,
    }, { emitEvent: false });
  }

  private getFieldOptions(searchParams: CarTypeRequest, field: NonNullable<CarTypeRequest['field']>): Observable<string[]> {
    return this.carService.getCarTypes(searchParams).pipe(
      map(options => this.extractFieldValues(options, field)),
      catchError(() => of([])),
    );
  }

  private extractFieldValues(options: CarTypeResponse[], field: NonNullable<CarTypeRequest['field']>): string[] {
    const values = options
      .map(option => option?.[field])
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    return Array.from(new Set(values)).sort((left, right) => left.localeCompare(right));
  }

  private trimValue(value: string = '') {
    return value.trim();
  }

  private getCarYears() {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];

    for (let year = currentYear + 1; year >= CreateLeadCarComponent.FIRST_CAR_YEAR; year--) {
      years.push(year.toString());
    }

    return years;
  }
}
